import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { CustomButton } from "../../../atoms/CustomButton";
import { AlertMessage } from "../../../atoms/AlertMessage";
import ConfirmModal from "../../../public/ConfirmModal";
import NoTranslate from "../../../atoms/NoTranslate";
import {
  Appointment,
  PaymentIssue,
  useCreatePaymentLink,
  useMarkPaymentIssueHandled,
} from "../../../../hooks/agenda";
import useAuth from "../../../../hooks/useAuth";
import {
  PaymentView,
  formatPrice,
  paymentPrice,
  paymentViewOf,
} from "./agendaPayment";

const BADGE_TONES = {
  pending: "warning",
  due: "warning",
  paid: "success",
} as const;

export const PaymentBadge: React.FC<{ view: PaymentView | null }> = ({
  view,
}) => {
  const { t } = useTranslation();
  if (!view) return null;
  return (
    <StatusBadge color={BADGE_TONES[view]} dot>
      {t(`agenda.payment.${view}`)}
    </StatusBadge>
  );
};

const LINK_REFUSALS: Record<string, string> = {
  PAYMENT_UNAVAILABLE: "agenda.payment.linkPayoutBlocked",
  NOT_DUE: "agenda.payment.notDue",
};

/** Where an appointment's payment stands, at the top of its window. */
export const AppointmentPaymentBar: React.FC<{
  appointment: Appointment;
  /** The agenda the appointment was read from, for an appointment one is not part of. */
  participant?: string | null;
  canCreateLink?: boolean;
}> = ({ appointment, participant, canCreateLink = false }) => {
  const { t, i18n } = useTranslation();
  const { user, customer_identification } = useAuth();
  const createLink = useCreatePaymentLink(
    customer_identification,
    user.entity_identification
  );
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [linkProblem, setLinkProblem] = useState<string | null>(null);
  const view = paymentViewOf(appointment);
  if (!view) return null;
  const payment = appointment.payment;
  const price = paymentPrice(payment, i18n.language);

  if (view === "pending") {
    return (
      <AlertMessage color="warning">
        {appointment.hold_expires_at
          ? t("agenda.payment.pendingUntil", {
              time: moment(appointment.hold_expires_at).format("LT"),
            })
          : t("agenda.payment.pending")}{" "}
        {t("agenda.payment.pendingLocked")}
      </AlertMessage>
    );
  }

  const link = view === "due" ? payment?.link || createdLink : null;
  const onCreateLink = () => {
    setLinkProblem(null);
    createLink.mutate(
      { id: appointment.id, participant },
      {
        onSuccess: (updated) => {
          if (updated?.payment?.link) setCreatedLink(updated.payment.link);
        },
        onError: (error) => {
          const reason =
            error?.response?.data?.element?.reason ??
            error?.response?.data?.code;
          setLinkProblem(LINK_REFUSALS[reason] ?? "agenda.payment.linkFailed");
        },
      }
    );
  };
  const copyLink = () => {
    if (!link) return;
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success(t("agenda.payment.linkCopied") as string))
      .catch(() => toast.error(t("agenda.payment.copyError") as string));
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-gray-200 px-3 py-2">
      <div className="flex min-w-0 flex-1 basis-40 flex-wrap items-center gap-x-3 gap-y-2">
        <PaymentBadge view={view} />
        {price && (
          <span className="text-sm font-medium tabular-nums text-gray-900">
            {price}
          </span>
        )}
      </div>
      {link ? (
        <CustomButton
          type="button"
          variant="secondary"
          size="sm"
          title={t("agenda.payment.copyLink") as string}
          onClick={copyLink}
        />
      ) : (
        view === "due" &&
        canCreateLink && (
          <CustomButton
            type="button"
            variant="secondary"
            size="sm"
            title={t("agenda.payment.createLink") as string}
            onClick={onCreateLink}
            isLoading={createLink.isLoading}
            disabled={createLink.isLoading}
          />
        )
      )}
      {linkProblem && (
        <p className="w-full text-xs text-red-600" role="alert">
          {t(linkProblem)}
        </p>
      )}
    </div>
  );
};

/** Payments that came in after their slot went to someone else: the business offers another time or refunds. */
const ISSUE_TEXTS = {
  taken: {
    title: "agenda.payment.issuesTitle",
    help: "agenda.payment.issuesHelp",
    handledAsked: "agenda.payment.issuesHandledAsked",
  },
  cancelled: {
    title: "agenda.payment.issuesCancelledTitle",
    help: "agenda.payment.issuesCancelledHelp",
    handledAsked: "agenda.payment.issuesCancelledHandledAsked",
  },
  duplicate: {
    title: "agenda.payment.issuesDuplicateTitle",
    help: "agenda.payment.issuesDuplicateHelp",
    handledAsked: "agenda.payment.issuesDuplicateHandledAsked",
  },
} as const;

type IssueReason = keyof typeof ISSUE_TEXTS;

const reasonOf = (issue: PaymentIssue): IssueReason => issue.reason ?? "taken";

export const PaymentIssuesAlert: React.FC<{
  issues: PaymentIssue[];
  /** The person whose alerts the viewer may mark as handled; none hides the button. */
  markableFor?: string | null;
}> = ({ issues, markableFor }) => {
  if (issues.length === 0) return null;
  const reasons = (Object.keys(ISSUE_TEXTS) as IssueReason[]).filter((reason) =>
    issues.some((issue) => reasonOf(issue) === reason)
  );
  return (
    <div className="space-y-2">
      {reasons.map((reason) => (
        <PaymentIssueGroup
          key={reason}
          reason={reason}
          issues={issues.filter((issue) => reasonOf(issue) === reason)}
          markableFor={markableFor}
        />
      ))}
    </div>
  );
};

const MarkIssueHandled: React.FC<{
  issue: PaymentIssue;
  reason: IssueReason;
  userId: string;
}> = ({ issue, reason, userId }) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const mark = useMarkPaymentIssueHandled(
    customer_identification,
    user.entity_identification,
    userId
  );
  const [asking, setAsking] = useState(false);
  const sent = useRef(false);
  const close = () => {
    if (mark.isLoading) return;
    mark.reset();
    setAsking(false);
  };
  const onConfirm = () => {
    if (sent.current) return;
    sent.current = true;
    mark.mutate(issue.id, {
      onSuccess: () => setAsking(false),
      onError: (error) => {
        if (error.response?.status === 404) setAsking(false);
      },
      onSettled: () => {
        sent.current = false;
      },
    });
  };
  return (
    <>
      <CustomButton
        type="button"
        variant="secondary"
        size="sm"
        title={t("agenda.payment.markHandled") as string}
        onClick={() => setAsking(true)}
      />
      <ConfirmModal
        show={asking}
        onClose={close}
        title={t("agenda.payment.markHandledTitle")}
        description={
          <span className="block space-y-2">
            <span className="block font-medium text-gray-900">
              <NoTranslate>{issue.title}</NoTranslate>
            </span>
            <span className="block">{t(ISSUE_TEXTS[reason].handledAsked)}</span>
            <span className="block">
              {t("agenda.payment.markHandledEffect")}
            </span>
            {mark.isError && (
              <span className="block text-red-600" role="alert">
                {t("agenda.payment.markHandledError")}
              </span>
            )}
          </span>
        }
        cancelBtnName={t("common.cancel")}
        cancelBtnFn={close}
        confirmBtnName={t("agenda.payment.markHandled")}
        confirmBtnFn={onConfirm}
        isLoading={mark.isLoading}
        disabled={mark.isLoading}
      />
    </>
  );
};

const PaymentIssueGroup: React.FC<{
  reason: IssueReason;
  issues: PaymentIssue[];
  markableFor?: string | null;
}> = ({ reason, issues, markableFor }) => {
  const { t, i18n } = useTranslation();
  return (
    <AlertMessage color="warning">
      <p className="font-medium">{t(ISSUE_TEXTS[reason].title)}</p>
      <ul className="mt-1 space-y-2">
        {issues.map((issue) => (
          <li
            key={issue.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1"
          >
            <span className="min-w-0 flex-1 basis-64 break-words">
              <NoTranslate>{issue.title}</NoTranslate>
              {" · "}
              {t("agenda.payment.issueLine", {
                date: moment(issue.start).format("lll"),
                price: formatPrice(issue.amount, issue.currency, i18n.language),
                paidAt: moment(issue.at).format("lll"),
              })}
              {issue.code && (
                <>
                  {" · "}
                  <NoTranslate>{issue.code}</NoTranslate>
                </>
              )}
            </span>
            {markableFor && (
              <MarkIssueHandled
                issue={issue}
                reason={reason}
                userId={markableFor}
              />
            )}
          </li>
        ))}
      </ul>
      <p className="mt-1">{t(ISSUE_TEXTS[reason].help)}</p>
    </AlertMessage>
  );
};
